import { Badge, MediumText, StrongText, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import type { ComponentProps } from 'react'
import { IMMUNIZATION_RECORDS, SCREENING_SCHEDULE } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_FAINT = uiClasses.textFaint
type BadgeColor = ComponentProps<typeof Badge>['variant']

const vaxStatusColor: Record<string, BadgeColor> = {
  'up-to-date': 'green',
  complete: 'emerald',
  recommended: 'amber',
}

const screeningStatusColor: Record<string, BadgeColor> = {
  'up-to-date': 'green',
  'not-yet-due': 'zinc',
  overdue: 'red',
}

function statusLabel(status: string): string {
  return status
    .split('-')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default function ImmunizationsTab() {
  const upToDate = IMMUNIZATION_RECORDS.filter((v) => v.status === 'up-to-date').length
  const recommended = IMMUNIZATION_RECORDS.filter((v) => v.status === 'recommended').length

  return (
    <div className="space-y-6">
      {/* Summary */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Summary</Subheading>
        <div className="mt-4 grid gap-6 sm:grid-cols-3">
          <GradientSphereMetricCard
            label="Vaccines on record"
            value={IMMUNIZATION_RECORDS.length}
            sphereColor={sphereColorAt(0)}
            valueSize="xl"
            labelVariant="section"
          />
          <GradientSphereMetricCard
            label="Up to date"
            value={upToDate}
            sphereColor={sphereColorAt(1)}
            valueSize="xl"
            labelVariant="section"
          />
          <GradientSphereMetricCard
            label="Recommended"
            value={recommended}
            sphereColor={sphereColorAt(2)}
            valueSize="xl"
            labelVariant="section"
          />
        </div>
      </SurfaceCard>

      {/* Immunization Records */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Immunization Records</Subheading>
        <div className="mt-4 space-y-3">
          {IMMUNIZATION_RECORDS.map((vax) => (
            <SurfaceRow key={vax.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StrongText>{vax.name}</StrongText>
                    <Badge variant={vaxStatusColor[vax.status] ?? 'zinc'}>
                      {statusLabel(vax.status)}
                    </Badge>
                  </div>
                  <p className={`mt-0.5 text-sm/6 ${TEXT_SECONDARY}`}>
                    {vax.date} · {vax.provider}
                  </p>
                  <p className={`mt-0.5 text-xs/5 ${TEXT_FAINT}`}>Lot: {vax.lot}</p>
                </div>
                <div className="text-right">
                  <p className={`text-xs/5 ${TEXT_FAINT}`}>Next due</p>
                  <MediumText tone="secondary">{vax.nextDue}</MediumText>
                </div>
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>

      {/* Preventive Screenings */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Preventive Screenings</Subheading>
        <div className="mt-4 space-y-3">
          {SCREENING_SCHEDULE.map((scr) => (
            <SurfaceRow key={scr.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StrongText>{scr.name}</StrongText>
                    <Badge variant={screeningStatusColor[scr.status] ?? 'zinc'}>
                      {statusLabel(scr.status)}
                    </Badge>
                  </div>
                  <p className={`mt-0.5 text-sm/6 ${TEXT_SECONDARY}`}>
                    {scr.frequency} · Last done: {scr.lastDone}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-xs/5 ${TEXT_FAINT}`}>Next due</p>
                  <MediumText tone="secondary">{scr.nextDue}</MediumText>
                </div>
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>
    </div>
  )
}
